import React from "react";
import { formatDate, formatDateTime } from "../../../../utils/datetime";
import { createTicketActivationHandler } from "../helpers";
import { DropDownList } from "../../../../components/ui";
import { getUserFullName } from "../../../../utils/business";

/**
 * Legacy: `onDelete` runs delete directly; `onDeleteRequest` opens a confirm modal.
 *
 * TicketCard - Default card component for TicketCardView
 * 
 * A generic card renderer that uses column configuration to display values.
 * Can be used as-is for simple cards or as a reference for custom card components.
 * 
 * @param {Object} props
 * @param {Object} props.item - The ticket/item data object
 * @param {Array} props.columns - Column configuration array
 * @param {Function} props.onCardClick - Callback when card is clicked
 * @param {Function} [props.getRowHref] - Optional app path for the card (enables middle-click / Ctrl+click new tab)
 * @param {Function} [props.onClone] - Optional callback when clone is clicked: (item) => void
 * @param {Function} [props.onDelete] - Legacy: optional callback that deletes immediately: (item) => void
 * @param {Array<React.ReactElement>|Function} [props.customActions] - Optional custom action components to render before the kebab menu.
 *   Can be an array of React elements or a function that receives the item and returns React elements: (item) => React.ReactElement[]
 * @param {number|string} [props.highlightAddedItemId] - When set and equal to item.id, card shows a brief green flash (e.g. after clone)
 * @param {number|string} [props.highlightDeletedItemId] - When set and equal to item.id, card shows a brief red flash (before delete)
 * @param {Function} [props.onDeleteRequest] - When provided, Delete triggers this (opens confirm modal) instead of onDelete
 * @returns {JSX.Element}
 */
const TicketCard = ({ item, columns, onCardClick, getRowHref, onClone, onDelete, onDeleteRequest, customActions, highlightAddedItemId, highlightDeletedItemId }) => {
  /**
   * Resolve a field's display value from the item using the column key,
   * optional `render`, and `format` (`date` / `datetime` / `currency`).
   *
   * @param {Object} column Column config (`key`, `render`, `format`, `emptyValue`, `dateFormat`).
   * @returns {React.ReactNode} Formatted content, or `emptyValue` / `"-"`.
   */
  const getValue = (column) => {
    if (column.render) {
      return column.render(item);
    }

    let value = column.key.split('.').reduce((obj, key) => obj?.[key], item);

    if (column.format && value != null && value !== "") {
      try {
        switch (column.format) {
          case "date":
            return formatDate(value, column.dateFormat || "mon DD, YYYY");
          case "datetime":
            return formatDateTime(value, column.dateFormat || "mon DD, YYYY");
          case "currency":
            return value
              ? `$${parseFloat(value).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : column.emptyValue || "-";
          default:
            return value;
        }
      } catch (e) {
        console.warn(`Failed to format value "${value}" for column "${column.key}":`, e);
        return column.emptyValue || "-";
      }
    }

    return value ?? column.emptyValue ?? "-";
  };

  // Find specific columns for card layout
  const idColumn = columns.find(col => col.key.includes('id') || col.key === 'ticket_id');
  const descColumn = columns.find(col => col.key === 'description' || col.key === 'subject');
  const priorityColumn = columns.find(col => col.key === 'priority');
  const statusColumn = columns.find(col => col.key === 'status');
  const otherColumns = columns.filter(col => 
    col.key !== idColumn?.key && 
    col.key !== descColumn?.key && 
    col.key !== priorityColumn?.key &&
    col.key !== statusColumn?.key &&
    col.key !== 'actions'
  );

  // Card activation (left click, middle click, Ctrl/Cmd+click); shared with TicketLogRow.
  const handleCardActivate = createTicketActivationHandler(
    item,
    getRowHref,
    onCardClick
  );

  /**
   * Build the card action chrome: custom buttons plus kebab Clone/Delete.
   * Legacy: `onDelete` runs delete directly; `onDeleteRequest` opens a confirm modal.
   *
   * @returns {JSX.Element|null} Action overlay, or null when there are no actions.
   */
  const renderActions = () => {
    const listItems = {};
    const disabledItems = [];
    
    // Add Clone option if handler provided
    if (onClone) {
      listItems["Clone"] = () => onClone(item);
    }
    
    // Add Delete option: onDeleteRequest opens confirm modal; onDelete runs delete directly (legacy)
    if (onDeleteRequest) {
      listItems["Delete"] = () => onDeleteRequest(item);
    } else if (onDelete) {
      listItems["Delete"] = () => onDelete(item);
    } else {
      listItems["Delete"] = () => {};
      disabledItems.push("Delete");
    }

    // Get custom actions - can be array or function
    let customActionsElements = [];
    if (customActions) {
      if (typeof customActions === 'function') {
        customActionsElements = customActions(item);
      } else if (Array.isArray(customActions)) {
        customActionsElements = customActions;
      }
    }

    // Only render if we have actions to show
    if (Object.keys(listItems).length === 0 && customActionsElements.length === 0) {
      return null;
    }

    return (
      <div 
        className="absolute top-2 right-2 flex gap-2 items-center z-10"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Custom actions (e.g., Edit, Price buttons) */}
        {customActionsElements.length > 0 && (
          <div 
            className="custom-actions-container flex gap-2 items-center"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {customActionsElements.map((action, index) => (
              <React.Fragment key={index}>
                {action}
              </React.Fragment>
            ))}
          </div>
        )}
        
        {/* Kebab menu dropdown */}
        {Object.keys(listItems).length > 0 && (
          <div 
            className="dropdown-actions-container"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <DropDownList
              buttonIcon="dotsVertical"
              listItems={listItems}
              disabledItems={disabledItems}
              align="right"
              className="relative"
              buttonClassName="text-gray-600 hover:text-gray-900"
            />
          </div>
        )}
      </div>
    );
  };

  const isHighlighted = highlightAddedItemId != null && item.id === parseInt(highlightAddedItemId, 10);
  const isDeletedHighlight = highlightDeletedItemId != null && item.id === parseInt(highlightDeletedItemId, 10);

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 hover:shadow-md transition-shadow cursor-pointer relative ${isHighlighted ? "animate-clone-flash" : ""} ${isDeletedHighlight ? "animate-delete-flash" : ""}`}
      onClick={handleCardActivate}
      onMouseDown={handleCardActivate}
      onAuxClick={handleCardActivate}
    >
      {/* Actions (kebab menu and custom actions) - top right */}
      {renderActions()}

      {/* Details in 3-column grid */}
      <div className="grid grid-cols-3 gap-x-3 gap-y-1.5 text-xs" style={{ gridAutoRows: 'max-content', rowGap: '4px' }}>
        {/* RFI number - row 1, spans 3 columns */}
        {idColumn && (
          <div 
            className="font-mono text-sm font-semibold text-blue-600 leading-tight"
            style={{ gridColumn: '1 / 4', gridRow: 1, maxHeight: '16px', overflow: 'hidden' }}
          >
            {getValue(idColumn)}
          </div>
        )}

        {/* Description - row 2, spans 2 columns */}
        {descColumn && (
          <p 
            className="text-sm font-medium text-gray-800 line-clamp-2 leading-tight"
            style={{ gridColumn: '1 / 3', gridRow: 2, maxHeight: '16px', overflow: 'hidden' }}
          >
            {getValue(descColumn) || "No description"}
          </p>
        )}

        {/* Priority label in third column, row 2 */}
        {priorityColumn && (
          <div 
            className="text-xs font-medium text-gray-500"
            style={{ gridColumn: 3, gridRow: 2, maxHeight: '16px', overflow: 'hidden' }}
          >
            Priority:
          </div>
        )}
        
        {/* Other fields in specific order: Column 1 (Project, Drawing, Spec), Column 2 (By, Submitted, Due) */}
        {(() => {
          // Define the order: left column fields first, then right column fields
          const leftColumnKeys = ['job_number', 'drawing_reference', 'spec_reference'];
          const rightColumnKeys = ['created_by', 'submitted_at', 'required_by_date'];
          
          // Find columns in the specified order
          const leftColumns = leftColumnKeys.map(key => otherColumns.find(col => col.key === key)).filter(Boolean);
          const rightColumns = rightColumnKeys.map(key => otherColumns.find(col => col.key === key)).filter(Boolean);
          
          // Render left column fields (col 1)
          const leftFields = leftColumns.map((column, index) => {
            const value = getValue(column);
            return (
              <div 
                key={column.key} 
                className="flex items-start gap-1.5 min-w-0"
                style={{ 
                  gridColumn: 1,
                  gridRow: index + 3, // Start from row 3
                  maxHeight: '16px',
                  overflow: 'hidden'
                }}
              >
                <span className="font-medium text-gray-500 flex-shrink-0">{column.label}:</span>
                <span className="text-gray-700 truncate min-w-0">
                  {value}
                </span>
              </div>
            );
          });
          
          // Render right column fields (col 2)
          const rightFields = rightColumns.map((column, index) => {
            let value = getValue(column);
            
            // For "created_by" field in card view, show full name instead of initials
            if (column.key === 'created_by' && item.created_by) {
              const worker = item.created_by;
              value = getUserFullName(worker) || '-';
            }
            
            return (
              <div 
                key={column.key} 
                className="flex items-start gap-1.5 min-w-0"
                style={{ 
                  gridColumn: 2,
                  gridRow: index + 3, // Start from row 3
                  maxHeight: '16px',
                  overflow: 'hidden'
                }}
              >
                <span className="font-medium text-gray-500 flex-shrink-0">{column.label}:</span>
                <span className="text-gray-700 truncate min-w-0">
                  {value}
                </span>
              </div>
            );
          });
          
          return [...leftFields, ...rightFields];
        })()}
        
        {/* Priority value in third column, row 3 (NOT spanning) */}
        {priorityColumn && (
          <div 
            className="flex-shrink-0"
            style={{ gridColumn: 3, gridRow: 3, maxHeight: '16px', overflow: 'hidden' }}
          >
            {getValue(priorityColumn)}
          </div>
        )}
        
        {/* Status label in third column, row 4 */}
        {statusColumn && (
          <div 
            className="text-xs font-medium text-gray-500"
            style={{ gridColumn: 3, gridRow: 4, maxHeight: '16px', overflow: 'hidden' }}
          >
            Status:
          </div>
        )}
        
        {/* Status value in third column, row 5 */}
        {statusColumn && (
          <div 
            className="flex-shrink-0"
            style={{ gridColumn: 3, gridRow: 5, maxHeight: '16px', overflow: 'hidden' }}
          >
            {getValue(statusColumn)}
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketCard;
